import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Compass, Check, ArrowLeft, CreditCard } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// One plan. $29.99 today covers the first 90 days; from day 91 Stripe bills
// $6.99/month automatically. Cancel any time from the billing portal and
// access runs to the end of the paid period.
const PLAN = {
  id: "standard",
  name: "Nomad Roadmap",
  price: "$29.99",
  period: "first 3 months",
  then: "then $6.99 / month, cancel anytime",
  icon: Compass,
  tagline: "Everything you need to plan one move, start to finish.",
  features: [
    "Up to 3 active country roadmaps",
    "Step-by-step application checklists",
    "Document organization vault",
    "Visa timer and deadline reminders",
    "Official links for every requirement",
  ],
  cta: "Start - $29.99",
};

const parseDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDate = (d) =>
  d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

const PricingPage = () => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const userData = await api.getMe();
        setUser(userData);
      } catch {}
    };
    checkAuth();
  }, []);

  const startCheckout = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    setBusy(true);
    try {
      const { url } = await api.createCheckout(PLAN.id);
      window.location.href = url;
    } catch (error) {
      toast.error(error.message || "Failed to start checkout");
      setBusy(false);
    }
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const { url } = await api.createPortalSession();
      window.location.href = url;
    } catch (error) {
      toast.error(error.message || "Could not open billing");
      setBusy(false);
    }
  };

  const expiry = parseDate(user?.core_access_expires);
  const active = !!user?.has_core_access && (!expiry || expiry > new Date());
  const cancelling = !!user?.cancel_at_period_end;

  const Icon = PLAN.icon;

  const renderAction = () => {
    if (!active) {
      return (
        <Button className="w-full h-12 rounded-xl" onClick={startCheckout} disabled={busy}>
          {busy ? "Loading..." : PLAN.cta}
        </Button>
      );
    }
    return (
      <div className="space-y-3">
        <div className="rounded-xl px-4 py-3 text-sm bg-primary/10 text-primary">
          {cancelling
            ? `Your plan ends ${expiry ? formatDate(expiry) : "at the end of this period"}.`
            : expiry
            ? `Active - next renewal ${formatDate(expiry)}.`
            : "Active."}
        </div>
        <Button className="w-full h-12 rounded-xl" variant="outline" onClick={openPortal} disabled={busy}>
          <CreditCard className="h-4 w-4 mr-2" />
          {busy ? "Loading..." : "Manage billing"}
        </Button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft />
            </Button>
            <Link to="/" className="font-bold text-lg">
              Nomad Roadmap
            </Link>
          </div>
          <Link to="/dashboard">
            <Button variant="outline" size="sm">
              Dashboard
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-20">
        <div className="text-center mb-14 animate-fade-in">
          <h1 className="text-3xl sm:text-5xl font-extrabold mb-4 font-['Manrope']">
            One plan. One price.
          </h1>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Three months to plan your move for $29.99. After that it's $6.99 a month to keep
            your roadmap, vault and reminders live - cancel whenever you're done.
          </p>
        </div>

        <div className="max-w-md mx-auto mb-20 animate-fade-in">
          <div className="relative flex flex-col rounded-2xl border border-accent p-8 shadow-lg ring-1 ring-accent/20">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <Badge className="bg-accent text-accent-foreground px-4 py-1">Launch pricing</Badge>
            </div>

            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
                <Icon className="h-5 w-5 text-primary" />
              </div>
              <h2 className="text-xl font-bold">{PLAN.name}</h2>
            </div>
            <p className="text-muted-foreground text-sm mb-6">{PLAN.tagline}</p>

            <div className="mb-1">
              <span className="text-4xl font-extrabold">{PLAN.price}</span>
              <span className="text-muted-foreground text-sm ml-2">/ {PLAN.period}</span>
            </div>
            <p className="text-sm text-muted-foreground mb-6">{PLAN.then}</p>

            <ul className="space-y-3 mb-8 flex-1">
              {PLAN.features.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <Check className="h-4 w-4 mt-0.5 text-primary" />
                  <span className="text-sm">{f}</span>
                </li>
              ))}
            </ul>

            {renderAction()}

            <p className="text-xs text-muted-foreground text-center mt-4">
              Secure checkout by Stripe. Cancel anytime from your profile.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PricingPage;
